import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requirePlatformAdmin?: boolean;
}

export function ProtectedRoute({ children, requirePlatformAdmin = false }: ProtectedRouteProps) {
  const { isAuthenticated, isPlatformAdmin, mustChangePassword, isLoading } = useAuth();
  const location = useLocation();

  // Wait for auth to load before making any decisions
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: 'var(--bg-primary, #f5f5f5)',
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e5e7eb',
            borderTopColor: '#006b3f',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <span style={{ color: '#6b7280', fontSize: '14px' }}>Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to landing page or platform login
    const redirectPath = requirePlatformAdmin ? '/platform/login' : '/welcome';
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // Check if user must change password and redirect to appropriate page
  if (mustChangePassword) {
    const forcePasswordPath = isPlatformAdmin
      ? '/platform/force-password-change'
      : '/force-password-change';

    // Don't redirect if already on the force password change page
    if (location.pathname !== forcePasswordPath) {
      return <Navigate to={forcePasswordPath} replace />;
    }
  }

  if (requirePlatformAdmin && !isPlatformAdmin) {
    // Non-platform admin trying to access platform routes
    return <Navigate to="/platform/login" replace />;
  }

  // If a platform admin tries to access institution routes, redirect to platform
  if (!requirePlatformAdmin && isPlatformAdmin) {
    return <Navigate to="/platform" replace />;
  }

  return <>{children}</>;
}
